import { PlusIcon } from '@shopify/polaris-icons';
import { Page, Modal, TextField, Select } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  FormLayout,
  Banner,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface BlockedDate {
  id: string;
  date: string;
  reason: string;
  block_type: 'full' | 'partial';
  affected_postcodes: string[] | null;
}

export default function BlockedDates() {
  const { fetchApi, error } = useApi();
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [modalActive, setModalActive] = useState(false);
  const [editingDate, setEditingDate] = useState<BlockedDate | null>(null);
  const [deletingDate, setDeletingDate] = useState<BlockedDate | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formDate, setFormDate] = useState('');
  const [formReason, setFormReason] = useState('');
  const [formBlockType, setFormBlockType] = useState('full');
  const [formPostcodes, setFormPostcodes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const postcodeList = formPostcodes.split(',').map((p) => p.trim()).filter(Boolean);
  const postcodesMissing = formBlockType === 'partial' && postcodeList.length === 0;
  const closeModal = () => { setModalActive(false); setFormError(null); setSubmitAttempted(false); };

  useEffect(() => {
    loadBlockedDates();
  }, []);

  async function loadBlockedDates() {
    const data = await fetchApi<{ blockedDates: BlockedDate[] }>('/rules/blocked-dates');
    if (data?.blockedDates) {
      setBlockedDates(data.blockedDates);
    }
  }

  const openNewModal = () => {
    setEditingDate(null);
    setFormDate('');
    setFormReason('');
    setFormBlockType('full');
    setFormPostcodes('');
    setModalActive(true);
  };

  const openEditModal = (blockedDate: BlockedDate) => {
    setEditingDate(blockedDate);
    setFormDate(blockedDate.date);
    setFormReason(blockedDate.reason || '');
    setFormBlockType(blockedDate.block_type);
    setFormPostcodes(blockedDate.affected_postcodes?.join(', ') || '');
    setModalActive(true);
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    setFormError(null);
    if (!formDate) { setFormError('Choose the date to block.'); return; }
    if (postcodesMissing) { setFormError('Enter at least one postcode prefix for a partial block, or choose "All Areas".'); return; }
    const data = {
      date: formDate,
      reason: formReason,
      block_type: formBlockType,
      affected_postcodes: formBlockType === 'partial' ? postcodeList : null,
    };

    setSaving(true);
    const result = await fetchApi(editingDate ? `/rules/blocked-dates/${editingDate.id}` : '/rules/blocked-dates', {
      method: editingDate ? 'PUT' : 'POST', body: JSON.stringify(data),
    });
    setSaving(false);
    if (result) { closeModal(); await loadBlockedDates(); }
    else setFormError('The blocked date could not be saved. Check the details below and try again.');
  };

  const handleDelete = async () => {
    if (!deletingDate) return;
    setSaving(true);
    const result = await fetchApi(`/rules/blocked-dates/${deletingDate.id}`, { method: 'DELETE' });
    setSaving(false);
    if (result) { setDeletingDate(null); await loadBlockedDates(); }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const isPast = (dateStr: string) => {
    return new Date(dateStr) < new Date();
  };

  // Separate past and upcoming
  const upcomingDates = blockedDates.filter((d) => !isPast(d.date));
  const pastDates = blockedDates.filter((d) => isPast(d.date));

  return (
    <Page
      title="Blocked Dates"
      primaryAction={{
        icon: PlusIcon, content: 'Add Blocked Date',
        onAction: openNewModal,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">Error: {error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Upcoming Blocked Dates</Text>
              <Text as="p" tone="subdued">
                Dates when deliveries are not available (e.g., bank holidays, closures).
              </Text>

              {upcomingDates.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Date', 'Reason', 'Type', 'Actions']}
                  rows={upcomingDates.map((d) => [
                    formatDate(d.date),
                    d.reason,
                    d.block_type === 'full' ? (
                      <Badge tone="critical">All Areas</Badge>
                    ) : (
                      <Badge tone="warning">Partial</Badge>
                    ),
                    <InlineStack gap="200">
                      <Button size="slim" onClick={() => openEditModal(d)}>
                        Edit
                      </Button>
                      <Button size="slim" tone="critical" onClick={() => setDeletingDate(d)}>
                        Delete
                      </Button>
                    </InlineStack>,
                  ])}
                />
              ) : (
                <Text as="p" tone="subdued">
                  No upcoming blocked dates.
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {pastDates.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Past Blocked Dates</Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'text']}
                  headings={['Date', 'Reason', 'Type']}
                  rows={pastDates.slice(0, 10).map((d) => [
                    formatDate(d.date),
                    d.reason,
                    d.block_type === 'full' ? 'All Areas' : 'Partial',
                  ])}
                />
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Quick Add Common Dates */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Quick Add</Text>
              <Text as="p" tone="subdued">
                Common blocked dates for UK businesses:
              </Text>
              <BlockStack gap="200">
                <Button icon={PlusIcon}
                  onClick={() => {
                    setFormDate(getNextChristmas());
                    setFormReason('Christmas Day');
                    setFormBlockType('full');
                    setModalActive(true);
                  }}
                >
                  Christmas Day
                </Button>
                <Button icon={PlusIcon}
                  onClick={() => {
                    setFormDate(getNextBoxingDay());
                    setFormReason('Boxing Day');
                    setFormBlockType('full');
                    setModalActive(true);
                  }}
                >
                  Boxing Day
                </Button>
                <Button icon={PlusIcon}
                  onClick={() => {
                    setFormDate(getNextNewYear());
                    setFormReason("New Year's Day");
                    setFormBlockType('full');
                    setModalActive(true);
                  }}
                >
                  New Year's Day
                </Button>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add/Edit Modal */}
      <Modal
        open={modalActive}
        onClose={closeModal}
        title={editingDate ? 'Edit Blocked Date' : 'Add Blocked Date'}
        primaryAction={{
          icon: editingDate ? undefined : PlusIcon, content: 'Save',
          onAction: handleSave,
          loading: saving,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {(formError || (submitAttempted && error)) && (
              <Banner tone="critical">{formError || error}</Banner>
            )}
            <TextField
              label="Date"
              type="date"
              requiredIndicator value={formDate}
              onChange={setFormDate}
              error={submitAttempted && !formDate ? 'Choose the date to block.' : undefined}
              autoComplete="off"
            />

            <TextField
              label="Reason"
              value={formReason}
              onChange={setFormReason}
              placeholder="e.g., Bank Holiday, Staff Training"
              autoComplete="off"
            />

            <Select
              label="Block Type"
              options={[
                { label: 'All Areas (Full)', value: 'full' },
                { label: 'Specific Postcodes (Partial)', value: 'partial' },
              ]}
              requiredIndicator value={formBlockType}
              onChange={setFormBlockType}
            />

            {formBlockType === 'partial' && (
              <TextField
                label="Affected Postcodes"
                requiredIndicator
                value={formPostcodes}
                onChange={setFormPostcodes}
                placeholder="e.g., SW1, SW2, EC1"
                helpText="Comma-separated list of postcode prefixes. Required for a partial block."
                error={submitAttempted && postcodesMissing ? 'Enter at least one postcode prefix.' : undefined}
                autoComplete="off"
              />
            )}
          </FormLayout>
        </Modal.Section>
      </Modal>
      <Modal open={!!deletingDate} title="Delete blocked date" onClose={() => { if (!saving) setDeletingDate(null); }} primaryAction={{ content: 'Delete blocked date', destructive: true, loading: saving, onAction: handleDelete }} secondaryActions={[{ content: 'Cancel', disabled: saving, onAction: () => setDeletingDate(null) }]}><Modal.Section><BlockStack gap="300">{error && <Banner tone="critical">{error}</Banner>}<Text as="p">Remove the block for {deletingDate?.date}? Delivery availability will be recalculated.</Text></BlockStack></Modal.Section></Modal>
    </Page>
  );
}

// Helper functions for quick add
function getNextChristmas(): string {
  const today = new Date();
  const year = today.getMonth() === 11 && today.getDate() > 25
    ? today.getFullYear() + 1
    : today.getFullYear();
  return `${year}-12-25`;
}

function getNextBoxingDay(): string {
  const today = new Date();
  const year = today.getMonth() === 11 && today.getDate() > 26
    ? today.getFullYear() + 1
    : today.getFullYear();
  return `${year}-12-26`;
}

function getNextNewYear(): string {
  const today = new Date();
  const year = today.getMonth() === 0 && today.getDate() > 1
    ? today.getFullYear() + 1
    : today.getFullYear() + 1;
  return `${year}-01-01`;
}
